import { useState } from "react";
import { ArrowDown, ArrowUp, Inbox, Loader2, Plus, Trash2 } from "lucide-react";
import { Sheet } from "@/ui";
import { cn } from "@/lib/cn";
import { useFormSubmissions } from "@/data/hooks";

/**
 * Forms as a product (capability #19). Two pieces, both attached to the Form
 * block's inspector:
 *  - FormFieldsEditor: a structured add/edit/reorder/remove editor for the
 *    form's `fields` array (replaces hand-editing raw JSON).
 *  - FormSubmissionsButton: opens a viewer of the form's real submissions
 *    (form-gateway GET /forms/{id}/submissions).
 */

const FIELD_TYPES = ["text", "email", "phone", "number", "date", "checkbox", "select", "textarea"] as const;
type FieldType = (typeof FIELD_TYPES)[number];

export interface FormField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

const input =
  "w-full rounded-btn border border-hairline bg-paper px-2 py-1 text-[12.5px] text-ink outline-none focus:border-accent/50 dark:bg-white/5";

/** Coerce an unknown JSON value into a FormField[] we can edit safely. */
function asFields(value: unknown): FormField[] {
  if (!Array.isArray(value)) return [];
  return value.map((f) => {
    const o = (f ?? {}) as Record<string, unknown>;
    const t = FIELD_TYPES.includes(o.type as FieldType) ? (o.type as FieldType) : "text";
    return {
      name: typeof o.name === "string" ? o.name : "",
      label: typeof o.label === "string" ? o.label : "",
      type: t,
      required: !!o.required,
      placeholder: typeof o.placeholder === "string" ? o.placeholder : undefined,
      options: Array.isArray(o.options) ? (o.options as unknown[]).map(String) : undefined,
    };
  });
}

/** slugify a label into a stable field name when the user hasn't set one. */
function slug(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "field";
}

export function FormFieldsEditor({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const fields = asFields(value);

  const commit = (next: FormField[]) =>
    onChange(
      next.map((f) => ({
        name: f.name || slug(f.label),
        label: f.label,
        type: f.type,
        required: !!f.required,
        ...(f.placeholder ? { placeholder: f.placeholder } : {}),
        ...(f.type === "select" && f.options?.length ? { options: f.options } : {}),
      })),
    );

  const update = (i: number, patch: Partial<FormField>) =>
    commit(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i: number) => commit(fields.filter((_, idx) => idx !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j]!, next[i]!];
    commit(next);
  };
  const add = () =>
    commit([...fields, { name: "", label: "New field", type: "text", required: false }]);

  return (
    <div className="space-y-2">
      <span className="block text-[11px] font-medium text-ink">Form fields</span>
      {fields.length === 0 && <p className="text-[12px] text-muted">No fields yet. Add the first one below.</p>}
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={i} className="rounded-btn border border-hairline bg-paper-2/40 p-2 dark:bg-white/5">
            <div className="mb-1.5 flex items-center gap-1.5">
              <input
                className={input}
                value={f.label}
                placeholder="Label"
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <button
                type="button"
                aria-label="Move up"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-subtle hover:text-ink disabled:opacity-30"
              >
                <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                aria-label="Move down"
                onClick={() => move(i, 1)}
                disabled={i === fields.length - 1}
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-subtle hover:text-ink disabled:opacity-30"
              >
                <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                aria-label="Remove field"
                onClick={() => remove(i)}
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-subtle hover:text-accent"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <select className={input} value={f.type} onChange={(e) => update(i, { type: e.target.value as FieldType })}>
                {FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                className={input}
                value={f.name}
                placeholder="name (auto)"
                onChange={(e) => update(i, { name: e.target.value })}
              />
            </div>
            {f.type !== "checkbox" && (
              <input
                className={cn(input, "mt-1.5")}
                value={f.placeholder ?? ""}
                placeholder="Placeholder (optional)"
                onChange={(e) => update(i, { placeholder: e.target.value || undefined })}
              />
            )}
            {f.type === "select" && (
              <input
                className={cn(input, "mt-1.5")}
                value={(f.options ?? []).join(", ")}
                placeholder="Options, comma-separated"
                onChange={(e) =>
                  update(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })
                }
              />
            )}
            <label className="mt-1.5 flex items-center gap-1.5 text-[12px] text-muted">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-hairline accent-accent"
                checked={!!f.required}
                onChange={(e) => update(i, { required: e.target.checked })}
              />
              Required
            </label>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 rounded-btn border border-hairline px-2.5 py-1.5 text-[12px] font-medium text-muted transition-colors hover:border-ink/25 hover:text-ink"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
        Add field
      </button>
    </div>
  );
}

export function FormSubmissionsButton({ formId }: { formId: string }) {
  const [open, setOpen] = useState(false);
  const q = useFormSubmissions(formId, open);
  const items = q.data?.items ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-btn border border-hairline px-2.5 py-1.5 text-[12px] font-medium text-muted transition-colors hover:border-ink/25 hover:text-ink"
      >
        <Inbox className="h-3.5 w-3.5" strokeWidth={1.75} />
        View submissions
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={`Submissions · ${formId}`}>
        {q.isLoading ? (
          <div className="flex items-center gap-2 py-8 text-[13px] text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : q.isError ? (
          <p className="py-8 text-[13px] text-accent">Couldn’t load submissions.</p>
        ) : items.length === 0 ? (
          <div className="grid place-items-center py-12 text-center">
            <Inbox className="mb-2 h-6 w-6 text-subtle" strokeWidth={1.5} />
            <p className="text-[14px] font-medium text-ink-strong">No submissions yet</p>
            <p className="mt-1 text-[13px] text-muted">They’ll appear here as this form is filled out.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[12px] text-muted">{items.length} submission{items.length === 1 ? "" : "s"}</p>
            {items.map((s) => (
              <div key={s.id} className="rounded-card border border-hairline bg-paper p-3 dark:bg-white/[0.03]">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="font-mono text-[10px] text-subtle">{new Date(s.submitted_at).toLocaleString()}</span>
                  {s.delivery_status && (
                    <span className="rounded-chip bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-subtle dark:bg-white/5">
                      {s.delivery_status}
                    </span>
                  )}
                </div>
                <dl className="space-y-0.5">
                  {Object.entries(s.values ?? {}).map(([k, v]) => (
                    <div key={k} className="flex gap-2 text-[12.5px]">
                      <dt className="shrink-0 font-medium text-muted">{k}</dt>
                      <dd className="min-w-0 break-words text-ink">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
      </Sheet>
    </>
  );
}
